import { Component, inject } from '@angular/core';
import { AlertController, IonApp, IonRouterOutlet, LoadingController } from '@ionic/angular/standalone';

import { CommonModule, NgIf } from '@angular/common';
import { Auth } from '@angular/fire/auth';
import { NavigationEnd, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { addIcons } from 'ionicons';
import { checkmarkCircle, close, closeCircle } from 'ionicons/icons';
import { filter } from 'rxjs';
import { register } from 'swiper/element/bundle';
import { CookieBannerComponent } from './components/cookie-banner/cookie-banner.component';
import { FooterMobileComponent } from './components/footer-mobile/footer-mobile.component';
import { HeaderComponent } from './components/header/header.component';
import { AnalyticsInitService } from './services/analytics-init.service';
import { AuthService } from './services/auth.service';
import { CategoryService } from './services/category.service';
import { CookieConsentService } from './services/cookie-consent.service';
import { CurrencyService } from './services/currency.service';
import { NotificationService } from './services/notification-service';
import { MarketService } from './services/market.service';
import { OrderService } from './services/order.service';
import { FaqService } from './services/faq.service';
import { ShoppingCartService } from './services/shopping-cart.service';
import { StorageService } from './services/storage.service';
import { TranslateService } from './services/translate.service';
import { UserService } from './services/user.service';
import { AlertUtils } from './utils/alert.utils';
import { LoadingUtils } from './utils/loading.utils';
register();

addIcons({ checkmarkCircle, closeCircle, close });

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    imports: [CommonModule, IonApp, IonRouterOutlet, FooterMobileComponent, HeaderComponent, CookieBannerComponent, NgIf],
})
export class AppComponent {
    firebaseAuth = inject(Auth);
    showHeader = false;

    constructor(
        private readonly storageService: StorageService,
        private readonly authService: AuthService,
        private readonly userService: UserService,
        private readonly router: Router,
        private readonly categoryService: CategoryService,
        private readonly translateService: TranslateService,
        private readonly orderService: OrderService,
        private readonly shoppingCartService: ShoppingCartService,
        private readonly exchangeRateService: CurrencyService,
        private readonly notificationService: NotificationService,
        private readonly loadingCtrl: LoadingController,
        private readonly alertCtrl: AlertController,
        private readonly cookieConsentService: CookieConsentService,
        private readonly analyticsInitService: AnalyticsInitService,
        private readonly faqService: FaqService,
        private readonly marketService: MarketService,
    ) {
        LoadingUtils.init(this.loadingCtrl, this.translateService);
        AlertUtils.init(this.alertCtrl, this.translateService);
        this.init();
        this.listenRouterEvents();
    }

    private async init(): Promise<void> {
        await this.storageService.init();
        this.marketService.init();
        await this.translateService.init();
        await this.exchangeRateService.init();
        await this.cookieConsentService.init();
        this.analyticsInitService.init();
        this.faqService.init();

        // Init capasitor plugins
        if (Capacitor.isNativePlatform()) {
            // Set status bar overlay
            // StatusBar.setOverlaysWebView({ overlay: true });
            StatusBar.setStyle({ style: Style.Light });
            // Hide splash screen after 3 seconds
            setTimeout(() => {
                SplashScreen.hide();
            }, 2000);
        }

        this.shoppingCartService.init();

        // Retrieve categories
        this.categoryService.populateCategories();

        // Retrieve vats
        this.orderService.getVats(this.marketService.active().country).subscribe();

        // Register push notifications
        if (Capacitor.isNativePlatform()) {
            await this.notificationService.init();
            await this.notificationService.register();
        }
    }

    private listenRouterEvents(): void {
        // NavigationEnd event, display header unless it's one of the following pages
        const hideHeaderPages = ['/platform'];
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            const url = event.urlAfterRedirects || event.url;
            if (url === '/') {
                this.showHeader = false;
                return;
            }
            this.showHeader = !hideHeaderPages.some((page) => url.startsWith(page));
        });
    }

    get backgroundClass(): string {
        return this.showHeader ? 'white' : 'light-grey';
    }
}
